import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AnchoredPortal from '../../../molecules/AnchoredPortal';
import { INSTRUMENTS } from '../../../../constants/Instruments';
import classNames from '../../../../utils/classNames';
import { resolveUserDisplayName } from '../../../../utils/userIdentity';

const normalizeInstrumentKey = (value) => (
  typeof value === 'string' ? value.trim().toLowerCase() : ''
);

export default function BandMemberInstrumentControl({
  member,
  onSaveInstrument,
}) {
  const { t } = useTranslation();
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const currentInstrument = typeof member?.mainInstrument === 'string'
    ? member.mainInstrument.trim()
    : '';
  const currentInstrumentKey = normalizeInstrumentKey(currentInstrument);
  const displayName = resolveUserDisplayName(member, '');
  const memberUserId = member?.userId || null;

  const filteredInstruments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return INSTRUMENTS;
    return INSTRUMENTS.filter((instrument) => (
      instrument.toLowerCase().includes(normalizedQuery)
    ));
  }, [query]);

  const closePicker = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSaveError(null);
  }, []);

  const handleTriggerClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isSaving) return;
    setSaveError(null);
    setIsOpen((current) => !current);
  }, [isSaving]);

  const handleSelectInstrument = useCallback(async (instrument) => {
    if (!memberUserId || !instrument || typeof onSaveInstrument !== 'function' || isSaving) {
      return;
    }
    if (normalizeInstrumentKey(instrument) === currentInstrumentKey) {
      closePicker();
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      await onSaveInstrument(memberUserId, instrument);
      closePicker();
    } catch (_error) {
      setSaveError(t('bandPage.members.mainInstrumentSaveError', {
        defaultValue: "We couldn't update that instrument. Please try again.",
      }));
    } finally {
      setIsSaving(false);
    }
  }, [
    closePicker,
    currentInstrumentKey,
    isSaving,
    memberUserId,
    onSaveInstrument,
    t,
  ]);

  const listboxLabel = t('bandPage.members.mainInstrumentSearchLabel', {
    defaultValue: 'Search instruments',
  });
  const buttonLabel = displayName
    ? t('bandPage.members.mainInstrumentAriaForMember', {
      name: displayName,
      defaultValue: `Change ${displayName} band main instrument`,
    })
    : t('bandPage.members.mainInstrumentAria', {
      defaultValue: 'Change band main instrument',
    });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={classNames(
          'band-members__instrument-trigger',
          'band-members__instrument-trigger--editable',
          isOpen && 'is-open',
        )}
        data-em-interactive="true"
        onClick={handleTriggerClick}
        aria-label={buttonLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={isSaving}
      >
        <span className="band-members__instrument-icon" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
        </span>
        <span className="band-members__instrument-text">
          <span className="band-members__instrument-eyebrow">
            {t('bandPage.members.instrumentLabel', { defaultValue: 'Instrument' })}
          </span>
          <span
            className={classNames(
              'band-members__instrument-value',
              !currentInstrument && 'band-members__instrument-value--empty',
            )}
          >
            {currentInstrument || t('bandPage.members.instrumentUnknown', {
              defaultValue: 'Set instrument',
            })}
          </span>
        </span>
        <span className="band-members__instrument-chevron" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {isOpen ? (
        <AnchoredPortal
          anchorRef={triggerRef}
          className="band-members__instrument-portal"
          placement="bottom-start"
          offset={{ x: 0, y: 8 }}
          gap={8}
          flip={false}
          onClose={closePicker}
        >
          <div
            className="band-members__instrument-menu"
            data-em-interactive="true"
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="search"
              className="band-members__instrument-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('bandPage.members.mainInstrumentSearchPlaceholder', {
                defaultValue: 'Search instrument...',
              })}
              aria-label={listboxLabel}
              disabled={isSaving}
              autoFocus
            />
            {saveError ? (
              <p className="band-members__instrument-error" role="alert">
                {saveError}
              </p>
            ) : null}
            <div
              className="band-members__instrument-list"
              role="listbox"
              aria-label={listboxLabel}
            >
              {filteredInstruments.length > 0 ? (
                filteredInstruments.map((instrument) => {
                  const isSelected = normalizeInstrumentKey(instrument) === currentInstrumentKey;
                  return (
                    <button
                      key={instrument}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={classNames(
                        'band-members__instrument-option',
                        isSelected && 'band-members__instrument-option--selected',
                      )}
                      onClick={() => handleSelectInstrument(instrument)}
                      disabled={isSaving}
                    >
                      <span>{instrument}</span>
                    </button>
                  );
                })
              ) : (
                <p className="band-members__instrument-empty">
                  {t('bandPage.members.mainInstrumentNoMatches', {
                    defaultValue: 'No instruments found.',
                  })}
                </p>
              )}
            </div>
          </div>
        </AnchoredPortal>
      ) : null}
    </>
  );
}

BandMemberInstrumentControl.propTypes = {
  member: PropTypes.shape({
    userId: PropTypes.string,
    username: PropTypes.string,
    displayName: PropTypes.string,
    mainInstrument: PropTypes.string,
  }).isRequired,
  onSaveInstrument: PropTypes.func.isRequired,
};
